// Global
import React from 'react';

// Local
import { KitchenTip, RecipeResponse } from 'lib/services/models/content-response';
import { RecipeSeo } from './RecipeSeo/RecipeSeo';
import { RecipeFAQ } from './RecipeFAQ/RecipeFAQ';
import { ActionToolbar } from 'components/helpers/ActionToolbar';
import styles from './RecipeDescriptionSeoContentSection.module.scss';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { Data } from '@generated/Foundation.GMI.model';
import { getActionToolbarButtons } from 'components/helpers/ActionToolbar/Utils';
import { RecipeDescription } from '../RecipeDescription/RecipeDescription';
import useDictionary from 'lib/hooks/useDictionary';

export interface RecipeDescriptionSeoContentSectionProps {
  recipe?: RecipeResponse;
  seoContent?: KitchenTip[];
  faqContent?: KitchenTip[];
  moreAboutRecipe?: KitchenTip[];
  faqSettings?: {
    hideRecipeFAQ?: boolean;
  };
  seoContentSettings?: {
    hideSEOContent?: boolean;
  };
}

export function RecipeDescriptionSeoContentSection(props: RecipeDescriptionSeoContentSectionProps) {
  const recipe = props.recipe;
  const siteSettings = useSiteSettings();
  const { getDictionaryValue } = useDictionary();

  const recipeDetailSettings =
    siteSettings?.recipeDetailSiteSettings as Data.Settings.RecipeDetailSiteSettings;

  const actionItems = getActionToolbarButtons(
    recipeDetailSettings?.fields?.seoActionToolBarButtons as
      | Data.Content.ActionToolBarButton[]
      | undefined
  );

  const seoActionToolbarIdentifier = recipeDetailSettings?.fields?.seoEventPosition?.value;

  const hideSeoContent = props?.seoContentSettings?.hideSEOContent;
  const hideFaqContent = props?.faqSettings?.hideRecipeFAQ;

  const hasSeoContent = (props.seoContent?.length ?? 0) > 0;
  const hasMoreAboutRecipe = (props.moreAboutRecipe?.length ?? 0) > 0;

  const moreAboutRecipeHeading =
    getDictionaryValue('Components.Recipe.MoreAboutThisRecipe.MoreAboutThisRecipeHeader') ||
    'More About This Recipe';

  // Render the whole wrapper only when SEO content is available and enabled .
  const shouldRenderSeoSection = !hideSeoContent && hasSeoContent;

  if (!recipe) {
    return <div>No recipe found</div>;
  }

  return (
    <div className={`${styles.recipeDescriptionSeoContentWrapper} printHide`}>
      <RecipeDescription recipe={recipe} />

      {shouldRenderSeoSection && (
        <>
          <hr className={styles.separator} />

          <div className={styles.recipeSeoContentWrapper}>
            <div className={styles.leftColumn}>
              {/* Action Toolbar */}
              {!hideSeoContent && (
                <ActionToolbar
                  className={styles.seoActionToolbar}
                  toolbarIdentifier={seoActionToolbarIdentifier}
                  actions={actionItems}
                  variant="vertical"
                />
              )}

              <div className={styles.sectionContent}>
                {/* SEO Content */}
                {!hideSeoContent && hasSeoContent && <RecipeSeo content={props.seoContent} />}

                {/* More About This Recipe */}
                {hasMoreAboutRecipe && (
                  <RecipeSeo content={props.moreAboutRecipe} title={moreAboutRecipeHeading} />
                )}

                {/* FAQ */}
                {!hideFaqContent && <RecipeFAQ {...props} />}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
